import { create } from "zustand";

/**
 * Keeps a teacher's live broadcast alive while they navigate the app.
 *
 * THE PROBLEM. `BroadcastStudio` owns the camera/mic tracks, the canvas
 * compositor, one RTCPeerConnection per viewer, the signalling socket and the
 * MediaRecorder — all in component refs, torn down by its unmount effect. So
 * clicking anything in the sidebar mid-lecture killed the broadcast for every
 * student. Teachers legitimately need to open the roll, a slide deck, or a
 * student's submission while talking.
 *
 * THE APPROACH. Rather than lift ~770 lines of intricate media code into a
 * provider (a large rewrite of a working pipeline, and a real regression risk
 * on something that cannot be fully exercised without live cameras), the studio
 * component is mounted ABOVE the router by `LiveSessionHost` and simply never
 * unmounts while a session is active. The studio ROUTE contributes only a
 * `mountNode`; the studio portals its full UI into that node when the teacher is
 * on the page, and renders a compact floating bar when they are anywhere else.
 *
 * The media pipeline therefore never learns that navigation happened — which is
 * exactly the property we want, and the reason this is safer than moving it.
 *
 * `session` is the intent to broadcast; `mountNode` is where to draw. They are
 * deliberately independent: session outlives the route, which is the whole point.
 */
export const useLiveSessionStore = create((set, get) => ({
  /** { courseId, broadcastId } while a studio session is open, else null. */
  session: null,
  /** The studio route's container element, or null when the teacher is elsewhere. */
  mountNode: null,

  /**
   * True only once the studio is actually ON AIR. Reported by BroadcastStudio.
   * `session` alone is not enough to refuse a switch — see openSession.
   */
  isLive: false,
  setLive: (v) => set({ isLive: !!v }),

  /**
   * Open (or re-focus) a studio session.
   *
   * Switching to a DIFFERENT broadcast is refused only while one is genuinely
   * LIVE: two concurrent captures would fight over the camera, and the server
   * rejects a second broadcaster anyway ("Someone is already broadcasting this
   * session").
   *
   * Refusing on `session` alone was too strict and stranded teachers: opening a
   * studio and NOT going live (a look at the settings, a device check, a wrong
   * link) left the session parked, and every other broadcast then refused to
   * open until a full page reload. A parked, never-live studio is simply
   * replaced.
   */
  openSession: ({ courseId, broadcastId }) => {
    const { session, isLive } = get();
    if (session && session.broadcastId === broadcastId) return true;   // re-focus
    if (session && isLive) return false;                               // genuinely busy
    set({ session: { courseId, broadcastId }, isLive: false });        // replace a parked one
    return true;
  },

  /** Called when the broadcast genuinely ends (or fails to start). */
  closeSession: () => set({ session: null, mountNode: null, isLive: false }),

  setMountNode: (node) => set({ mountNode: node }),

  /** True when a session is running but the teacher has navigated away from it. */
  isDetached: () => !!get().session && !get().mountNode,
}));
